import { Injectable, inject } from '@angular/core';
import {
  Firestore, collection, addDoc, doc, getDoc, query, where, limit, getDocs, runTransaction,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { Asset, ScheduleException } from '../models/asset.model';
import { BookingStatus } from '../models/booking.model';

/** PII-free availability mirror doc (written by Cloud Functions only). */
export interface SpaceAvailabilityRecord {
  spaceId:   string;
  date:      string;       // YYYY-MM-DD
  startTime: string;       // HH:MM
  endTime:   string;       // HH:MM
  status:    BookingStatus;
  /** Present on auto-generated MANUAL_BLOCK cleaning records (e.g. 'Limpieza'). */
  label?:    string;
}
import { DEFAULT_CONTRACT_TEMPLATE_HTML } from '../constants/default-contract-template';
import { fromCollectionSnapshot, fromDocSnapshot } from '../shared/firestore/live-query';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';
import { spaceMatchesPublicSlug } from '../shared/location/space-public-url.utils';

@Injectable({ providedIn: 'root' })
export class AssetService extends FirebaseAwareService {
  private firestore = inject(Firestore);

  getAssetsByOwner(uid: string): Observable<Asset[]> {
    const q = this.inCtx(() =>
      query(collection(this.firestore, 'assets'), where('ownerId', '==', uid)),
    );
    return fromCollectionSnapshot<Asset>(this.injector, q);
  }

  getAllSpaces(): Observable<Asset[]> {
    const q = this.inCtx(() =>
      query(collection(this.firestore, 'assets'), where('active', '==', true)),
    );
    return fromCollectionSnapshot<Asset>(this.injector, q);
  }

  /** Live stream of a single space document. */
  watchSpace(spaceId: string): Observable<Asset | null> {
    const docRef = this.inCtx(() => doc(this.firestore, 'assets', spaceId));
    return fromDocSnapshot<Asset | null>(
      this.injector,
      docRef,
      snap => snap.exists() ? { id: snap.id, ...snap.data() } as Asset : null,
    );
  }

  /** Public profile page: all active spaces for a given owner. */
  getActiveSpacesByOwner(ownerUid: string): Promise<Asset[]> {
    return this.inCtx(() =>
      getDocs(
        query(
          collection(this.firestore, 'assets'),
          where('ownerId', '==', ownerUid),
          where('active', '==', true),
        ),
      ),
    ).then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() } as Asset)));
  }

  /** Public: last `count` active spaces ordered by creation (newest first). */
  getLatestSpaces(count = 6): Promise<Asset[]> {
    return this.inCtx(() =>
      getDocs(
        query(
          collection(this.firestore, 'assets'),
          where('active', '==', true),
          limit(count),
        ),
      ),
    ).then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() } as Asset)));
  }

  createSpaceDraft(data: Omit<Asset, 'id'>): Promise<string> {
    if (!data.ownerId) throw new Error('ownerId is required');
    return this.inCtx(() =>
      addDoc(collection(this.firestore, 'assets'), { ...data, active: false }),
    ).then(ref => ref.id);
  }

  async updateSpace(spaceId: string, data: Partial<Omit<Asset, 'id'>>): Promise<void> {
    await this.inCtx(() => {
      const ref = doc(this.firestore, 'assets', spaceId);
      return runTransaction(this.firestore, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) {
          throw new Error('Space not found');
        }
        tx.update(ref, { ...data });
      });
    });
  }

  async ensureDefaultContractTemplate(): Promise<string> {
    const currentSnap = await this.inCtx(() =>
      getDoc(doc(this.firestore, 'config', 'globalSettings')),
    );
    const currentData = currentSnap.exists() ? (currentSnap.data() as { defaultContractTemplateHtml?: unknown }) : null;
    const currentTemplate = typeof currentData?.defaultContractTemplateHtml === 'string'
      ? currentData.defaultContractTemplateHtml
      : '';
    if (currentTemplate.trim()) {
      return currentTemplate;
    }

    const legacySnap = await this.inCtx(() =>
      getDoc(doc(this.firestore, 'platformConfig', 'defaultLeaseTemplate')),
    );
    const legacyData = legacySnap.exists() ? (legacySnap.data() as { templateHtml?: unknown }) : null;
    const legacyTemplate = typeof legacyData?.templateHtml === 'string'
      ? legacyData.templateHtml
      : '';
    if (legacyTemplate.trim()) {
      return legacyTemplate;
    }

    return DEFAULT_CONTRACT_TEMPLATE_HTML;
  }

  getSpaceById(spaceId: string): Promise<Asset | null> {
    return this.inCtx(() => getDoc(doc(this.firestore, 'assets', spaceId)))
      .then(snap => {
        if (!snap.exists()) return null;
        return { id: snap.id, ...snap.data() } as Asset;
      });
  }

  /** Public availability mirror — no PII, readable without auth (see firestore.rules). */
  getAvailabilityForSpace(spaceId: string): Promise<SpaceAvailabilityRecord[]> {
    return this.inCtx(() =>
      getDocs(collection(this.firestore, `assets/${spaceId}/availability`)),
    ).then(snap => snap.docs.map(d => ({ ...d.data() } as SpaceAvailabilityRecord)));
  }

  /** One-time releases of recurring fixed-block slots. Public read (see firestore.rules). */
  getScheduleExceptions(spaceId: string): Promise<ScheduleException[]> {
    return this.inCtx(() =>
      getDocs(collection(this.firestore, `assets/${spaceId}/scheduleExceptions`)),
    ).then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() } as ScheduleException)));
  }

  /**
   * Resolves a public SEO path (`slugCompleto`) to a space document.
   * Queries active spaces only (required by Firestore rules for guests).
   * Falls back to matching computed slugs when `slugCompleto` is not persisted yet.
   */
  async getSpaceBySlugCompleto(slugCompleto: string): Promise<Asset | null> {
    const slug = slugCompleto.trim();
    if (!slug) return null;

    const toAsset = (id: string, data: Record<string, unknown>) => ({ id, ...data } as Asset);

    try {
      const exactSnap = await this.inCtx(() =>
        getDocs(
          query(
            collection(this.firestore, 'assets'),
            where('slugCompleto', '==', slug),
            where('active', '==', true),
            limit(2),
          ),
        ),
      );
      if (!exactSnap.empty) {
        const d = exactSnap.docs[0];
        return toAsset(d.id, d.data());
      }
    } catch {
      // Query may be rejected without active filter; continue to fallback scan.
    }

    const activeSnap = await this.inCtx(() =>
      getDocs(query(collection(this.firestore, 'assets'), where('active', '==', true))),
    );
    const matches = activeSnap.docs
      .map(d => toAsset(d.id, d.data()))
      .filter(s => spaceMatchesPublicSlug(s, slug));

    if (matches.length === 0) return null;
    return matches.find(s => s.slugCompleto?.trim() === slug) ?? matches[0];
  }

  /** Owner-only: resolve SEO slug among the caller's spaces (includes drafts). */
  async getOwnerSpaceByPublicSlug(ownerUid: string, slugCompleto: string): Promise<Asset | null> {
    const slug = slugCompleto.trim();
    if (!slug || !ownerUid) return null;
    const ownerSnap = await this.inCtx(() =>
      getDocs(query(collection(this.firestore, 'assets'), where('ownerId', '==', ownerUid))),
    );
    const matches = ownerSnap.docs
      .map(d => ({ id: d.id, ...d.data() } as Asset))
      .filter(s => spaceMatchesPublicSlug(s, slug));
    if (matches.length === 0) return null;
    return matches.find(s => s.slugCompleto?.trim() === slug) ?? matches[0];
  }

}
